"use client";

import type { NotificationObject } from "@golf-district/shared";
import { FilledButton } from "~/components/buttons/filled-button";
import { FilterSort } from "~/components/buttons/filters-sort";
import { GoBack } from "~/components/buttons/go-back";
import { CourseBanner } from "~/components/course-page/course-banner";
import { DailyTeeTimes } from "~/components/course-page/daily-tee-times";
import { Filters } from "~/components/course-page/filters";
import { MobileDates } from "~/components/course-page/mobile-date";
import { MobileFilters } from "~/components/course-page/mobile-filters";
import { MobileThemePicker } from "~/components/course-page/mobile-theme-picker";
import { DesktopThemeSelect } from "~/components/course-page/desktop-theme-select";
import { MobileSort, SortOptions } from "~/components/course-page/mobile-sort";
import { Calendar } from "~/components/icons/calendar";
import { FiltersIcon } from "~/components/icons/filters";
import { ChevronUp } from "~/components/icons/chevron-up";
import { Select } from "~/components/input/select";
import { ForecastModal } from "~/components/modal/forecast-modal";
import { useAppContext } from "~/contexts/AppContext";
import { useBookingSourceContext } from "~/contexts/BookingSourceContext";
import { useCourseContext } from "~/contexts/CourseContext";
import type { DateType, GolferType } from "~/contexts/FiltersContext";
import { useFiltersContext } from "~/contexts/FiltersContext";
import { useUserContext } from "~/contexts/UserContext";
import { api } from "~/utils/api";
import { microsoftClarityEvent } from "~/utils/microsoftClarityUtils";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import RelativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Weekday from "dayjs/plugin/weekday";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ViewportList } from "react-viewport-list";
import { useIsMobile } from "~/hooks/useIsMobile";
import { useCustomThemeContext } from "~/contexts/CustomThemeContext";
import { LoadingContainer } from "./loader";
import { DailyTeeTimesMobileV2 } from "~/components/course-page/daily-tee-times-mobile-v2";
import { DailyTeeTimesDesktopV2 } from "~/components/course-page/daily-tee-time-desktop-v2";

dayjs.extend(Weekday);
dayjs.extend(RelativeTime);
dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
export default function CourseHomePage() {
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const queryDateType = searchParams.get("dateType");
  const queryDate = searchParams.get("date");
  const queryStartTime = searchParams.get("startTime");
  const queryEndTime = searchParams.get("endTime");
  const queryPlayerCount = searchParams.get("playerCount");
  const source = searchParams.get("source");
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showSort, setShowSort] = useState<boolean>(false);
  const [showDates, setShowDates] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const [isForecastModalOpen, setIsForecastModalOpen] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  // const [hasUserInteractedWithFilters, setHasUserInteractedWithFilters] = useState<boolean>(false);
  // const isInitialMountRef = useRef(true);
  // const filterChangeCountRef = useRef(0);
  const { user } = useUserContext();
  const { course } = useCourseContext();
  const ALLOW_COURSE_SWITCHING = course?.isAllowCourseSwitching;
  const { setBookingSource } = useBookingSourceContext();
  const { isNavExpanded, setActivePage } = useAppContext();

  useEffect(() => {
    setActivePage("teeTime");
  }, [setActivePage]);


  function getUserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  function compareTimesWithTimezones() {
    const date1 = dayjs().tz(getUserTimezone()).utcOffset()
    const date2 = dayjs().tz(course?.timezoneISO).utcOffset()

    if (date1 > date2) {
      return "user";
    } else if (date1 < date2) {
      return "course";
    } else {
      return "user";
    }
  }

  const formatDateString = (
    date: string | number | Date | Dayjs | null | undefined
  ): string => {
    if (!date) {
      return ""; // Handle the case where date is null or undefined
    }
    const compareTimeZone = compareTimesWithTimezones()

    if (compareTimeZone === "user") {
      return dayjs(date).format("ddd, DD MMM YYYY HH:mm:ss [GMT]");
    }
    return dayjs(date)
      .tz(course?.timezoneISO)
      .format("ddd, DD MMM YYYY HH:mm:ss [GMT]");
  };

  const formatDateStringEnd = (
    date: string | number | Date | Dayjs | null | undefined
  ): string => {
    if (!date) {
      return ""; // Handle the case where date is null or undefined
    }
    return dayjs(date)
      .hour(23)
      .minute(59)
      .second(59)
      .millisecond(999)
      .format("ddd, DD MMM YYYY HH:mm:ss [GMT]");
  };

  const {
    showUnlisted,
    includesCart,
    golfers,
    holes,
    priceRange,
    startTime,
    sortValue,
    dateType,
    selectedDay,
    handleSetSortValue,
    setDateType,
    setSelectedDay,
    setStartTime,
    setGolfers,
  } = useFiltersContext();
  const { entity, alertOffersShown, setAlertOffersShown } = useAppContext();
  const router = useRouter();
  const courseId = course?.id;
  const { data: MOBILE_VIEW_VERSION } =
    api.course.getMobileViewVersion.useQuery({
      courseId: courseId ?? "",
    });

  const entityId = entity?.id;

  const { data: allCoursesData, isLoading: allCoursesDataLoading } =
    api.entity.getCoursesByEntityId.useQuery(
      { entityId: entityId! },
      {
        enabled: entityId !== undefined,
        refetchOnWindowFocus: false,
        keepPreviousData: true
      }
    );

  const { data: allSwitchCoursesData } = api.course.getAllSwitchCourses.useQuery({ courseId: course?.id ?? "" })

  const { data: DESKTOP_VIEW_VERSION } =
    api.course.getDesktopViewVersion.useQuery({
      courseId: courseId ?? "",
    });

  const TAKE = MOBILE_VIEW_VERSION === "v2" && isMobile ? 1 : 4;
  const [take, setTake] = useState<number>(TAKE);

  const { customThemes, selectedThemeId, selectedTheme, setSelectedThemeId, courseCss } = useCustomThemeContext();

  // Tracked wrapper used by both the mobile + desktop theme pickers.
  const handleThemeSelect = (id: string) => {
    googleAnalyticsEvent({
      action: "dd_ttlist_theme",
      params: { selected_value: id },
    });
    setSelectedThemeId(id);
  };

  // When the course has its own CSS, prepend a "Default" option representing the course theme.
  // Filter out any custom theme also named "Default" to avoid duplicates.
  const DEFAULT_THEME = { id: "", name: "Default", css: null, activeFlag: true, iconUrl: null };
  const themesForPicker = customThemes && customThemes.length > 0
    ? [DEFAULT_THEME, ...customThemes.filter((t) => t.name.toLowerCase() !== "default")]
    : [];
  const selectedThemeForPicker = selectedThemeId === "" ? DEFAULT_THEME : selectedTheme;



  const updateUser = api.user.updateUser.useMutation();
  const { data: specialEvents, isLoading: specialEventsLoading } =
    api.searchRouter.getSpecialEvents.useQuery({
      courseId: courseId ?? "",
    });

  const farthestDateOut = useMemo(() => {
    return dayjs()
      .utc()
      .add(course?.furthestDayToBook ?? 0, "day")
      .format("YYYY-MM-DD");
  }, [course?.furthestDayToBook]);

  const { data: unreadOffers } = api.user.getUnreadOffersForCourse.useQuery(
    {
      courseId: courseId ?? "",
    },
    {
      enabled: courseId !== undefined && user?.id !== undefined,
    }
  );

  // const getUtcDate = (
  //   date: string | number | Dayjs | Date | null | undefined,
  //   timezoneCorrection = 0
  // ): string => {
  //   const currentDate = dayjs.utc(formatDateString(date));
  //   return currentDate.add(timezoneCorrection, "hour").toString();
  // };

  // useEffect(() => {
  //   if (isInitialMountRef.current) {
  //     filterChangeCountRef.current += 1;
  //     if (filterChangeCountRef.current > 3) {
  //       isInitialMountRef.current = false;
  //     }
  //     return;
  //   }
  //   setHasUserInteractedWithFilters(true);
  // }, [golfers, holes, startTime[0], startTime[1], priceRange[0], priceRange[1], dateType, includesCart]);

  useEffect(() => {
    if (queryDateType === "custom" && queryDate) {
      // setHasUserInteractedWithFilters(true);
      setDateType("Select Dates");

      // const courseOpenTime = Number(dayjs(course?.openTime).format("HHmm"));
      // const courseCloseTime = Number(dayjs(course?.closeTime).format("HHmm"));
      const courseOpenTime = course?.courseOpenTime ?? 9;
      const courseCloseTime = course?.courseCloseTime ?? 9;
      const qStartTime = Math.max(courseOpenTime, Number(queryStartTime));
      const qEndTime = Math.min(courseCloseTime, Number(queryEndTime));
      setStartTime([qStartTime, qEndTime]);

      const playerCount =
        Number(queryPlayerCount) <= 0 || Number(queryPlayerCount) > 4
          ? "Any"
          : Number(queryPlayerCount);
      setGolfers((playerCount as GolferType) || "Any");

      const [year, month, day] = queryDate.split("-");
      if (year && month && day) {
        setSelectedDay({
          from: { year: Number(year), month: Number(month), day: Number(day) },
          to: { year: Number(year), month: Number(month), day: Number(day) },
        });
      }
      const specialDate = getSpecialDayDate(queryDateType);
      if (queryDateType && specialDate) {
        setDateType(queryDateType as DateType);
      }
    }
  }, [queryDateType, queryDate, specialEvents]);

  const getSpecialDayDate = (label: string) => {
    const today = dayjs(new Date());
    const specialDay = specialEvents?.find((day) => day.eventName === label);

    if (specialDay) {
      const specialStartDate = dayjs(specialDay.startDate);

      const start = today.isAfter(specialStartDate) ? today : specialStartDate;

      return {
        start: start,
        end: specialDay.endDate ? dayjs(specialDay.endDate) : null,
      };
    }

    return null;
  };

  const startDate = useMemo(() => {
    const specialDate = getSpecialDayDate(dateType);
    if (specialDate) {
      return formatDateString(specialDate.start);
    }
    setPageNumber(1);
    switch (dateType) {
      case "All": {
        const currentTime = dayjs(new Date());
        const currentTimePlus30 = currentTime.add(30, 'minute');

        const closingHour = Math.floor(startTime[1] / 100);
        const closingMinute = startTime[1] % 100;

        const closingTime = currentTime.set('hour', closingHour).set('minute', closingMinute).set('second', 0);

        if (currentTimePlus30.isAfter(closingTime)) {
          return formatDateString(currentTime.add(1, 'day').startOf('day'))
        }

        return formatDateString(currentTimePlus30);
      }
      // case "This Week": {
      //   if (isMobile) {
      //     const currentTime = dayjs(new Date());
      //     const currentTimePlus30 = currentTime.add(30, 'minute');

      //     const closingHour = Math.floor(startTime[1] / 100);
      //     const closingMinute = startTime[1] % 100;

      //     const closingTime = currentTime.set('hour', closingHour).set('minute', closingMinute).set('second', 0);

      //     if (currentTimePlus30.isAfter(closingTime)) {
      //       return formatDateString(currentTime.add(1, 'day').startOf('day'))
      //     }

      //     return formatDateString(currentTimePlus30);
      //   }
      // }
      // eslint-disable-next-line no-fallthrough
      // case "This Month": {
      //   if (isMobile) {
      //     const currentTime = dayjs(new Date());
      //     const currentTimePlus30 = currentTime.add(30, 'minute');

      //     const closingHour = Math.floor(startTime[1] / 100);
      //     const closingMinute = startTime[1] % 100;

      //     const closingTime = currentTime.set('hour', closingHour).set('minute', closingMinute).set('second', 0);

      //     if (currentTimePlus30.isAfter(closingTime)) {
      //       return formatDateString(currentTime.add(1, 'day').startOf('day'))
      //     }

      //     return formatDateString(currentTimePlus30);
      //   }
      // }

      case "Furthest Day Out To Book":
        return formatDateString(dayjs(new Date()).add(30, "minute"));
      case "Today": {
        const currentTime = dayjs();
        const currentTimePlus30 = currentTime.add(30, 'minute');

        const closingHour = Math.floor(startTime[1] / 100);
        const closingMinute = startTime[1] % 100;

        const closingTime = currentTime.set('hour', closingHour).set('minute', closingMinute).set('second', 0);

        if (currentTimePlus30.isAfter(closingTime)) {
          return formatDateString(currentTime.add(1, 'day').startOf('day').add(30, 'minute'));
        }

        return formatDateString(currentTimePlus30);
      }
      case "This Weekend": {
        if (isMobile) {
          const today = dayjs().startOf("day");
          const weekend = dayjs().day(5); // This Friday

          const currentTime = dayjs();
          const currentTimePlus30 = currentTime.add(30, "minute");

          const closingHour = Math.floor(startTime[1] / 100);
          const closingMinute = startTime[1] % 100;

          const closingTime = currentTime.set("hour", closingHour).set("minute", closingMinute).set("second", 0);

          // If today is Friday or later
          if (weekend.isSame(today, "day") || weekend.isBefore(today, "day")) {
            if (currentTimePlus30.isAfter(closingTime)) {
              return formatDateString(currentTime.add(1, "day").startOf("day"));
            }
            return formatDateString(currentTimePlus30);
          }

          // Weekend is in the future
          return formatDateString(weekend.startOf("day").toDate());
        }
      }
      // eslint-disable-next-line no-fallthrough
      case "Select Dates": {
        if (!selectedDay.from) return formatDateString(new Date());
        const customDate = dayjs(
          `${selectedDay.from.year}-${selectedDay.from.month}-${selectedDay.from.day}`
        ).startOf("day");
        const today = dayjs().startOf("day");
        if (customDate.isSame(today)) {
          return formatDateString(dayjs(new Date()).add(30, "minute"));
        }
        return formatDateString(customDate);
      }
      default:
        return formatDateString(new Date());
    }
  }, [dateType, selectedDay, queryDateType, specialEvents, startTime, isMobile]);

  const endDate = useMemo(() => {
    const specialDate = getSpecialDayDate(dateType);
    if (specialDate) {
      return formatDateString(specialDate.end);
    }
    setPageNumber(1);
    switch (dateType) {
      case "All":
        return formatDateStringEnd(farthestDateOut);
      case "Today":
        {
          const currentTime = dayjs();
          const currentTimePlus30 = currentTime.add(30, 'minute');

          const closingHour = Math.floor(startTime[1] / 100);
          const closingMinute = startTime[1] % 100;

          const closingTime = currentTime.set('hour', closingHour).set('minute', closingMinute).set('second', 0);

          if (currentTimePlus30.isAfter(closingTime)) {
            return formatDateStringEnd(dayjs().add(1, "day").endOf("day").toDate());
          }

          return formatDateStringEnd(dayjs().endOf("day").toDate());
        }
      // case "This Week": {
      //   const currentDay = dayjs();
      //   const isSunday = currentDay.day() === 0;

      //   if (isSunday) {
      //     return formatDateStringEnd(currentDay.add(1, 'week').endOf('isoWeek').toDate());
      //   }

      //   return formatDateStringEnd(currentDay.endOf('isoWeek').toDate());
      // }
      case "This Weekend": {
        const currentDay = dayjs();
        const isSunday = currentDay.day() === 0;

        if (isSunday) {
          return formatDateStringEnd(currentDay.add(1, 'week').endOf('isoWeek'));
        }

        return formatDateStringEnd(currentDay.day(7).endOf('day'));
      }
      // case "This Month": {
      //   const today = dayjs();
      //   const lastDayOfMonth = today.endOf('month');
      //   if (today.isSame(lastDayOfMonth, 'day')) {
      //     return formatDateStringEnd(lastDayOfMonth.add(1, 'month').endOf('month').toDate());
      //   }

      //   return formatDateStringEnd(lastDayOfMonth.toDate());
      // }
      case "Furthest Day Out To Book":
        return formatDateStringEnd(dayjs(farthestDateOut).toDate());
      case "Select Dates": {
        if (!selectedDay.to) {
          if (selectedDay.from) {
            const endOfDay = dayjs(
              `${selectedDay.from.year}-${selectedDay.from.month}-${selectedDay.from.day}`
            ).endOf("day");
            return formatDateStringEnd(endOfDay);
          }
          return formatDateStringEnd(dayjs().endOf("day").toDate());
        }
        const endDateCustom = dayjs(
          `${selectedDay.to.year}-${selectedDay.to.month}-${selectedDay.to.day}`
        ).endOf("day");
        return formatDateStringEnd(endDateCustom);
      }
      default:
        return formatDateString(dayjs().add(360, "days").toDate());
    }
  }, [dateType, selectedDay, farthestDateOut, specialEvents, startTime]);

  // const utcStartDate = dayjs
  //   .utc(startDate)
  //   .utcOffset(course?.timezoneCorrection ?? 0);

  const utcEndDate = dayjs
    .utc(endDate)
    .utcOffset(course?.timezoneCorrection ?? 0);

  const daysData = useMemo(() => {
    const amountOfDays = dayjs(utcEndDate).diff(startDate, "day");
    const daysToTake = amountOfDays;
    const arrayOfDates: string[] = [];

    for (let i = 0; i <= daysToTake; i++) {
      if (dateType === "Furthest Day Out To Book") {
        let date = utcEndDate.startOf("day");
        date = date.subtract(i, "day");
        // if day is today, stop loop
        if (date.add(1, "day").isSame(dayjs(), "day")) break;
        arrayOfDates.push(date.toString());
        continue;
      } else {
        const date = dayjs(startDate).add(i, "day");
        arrayOfDates.push(date.toString());
        continue;
      }
    }

    const amountOfPages = Math.ceil((amountOfDays + 1) / TAKE);
    return { arrayOfDates, amountOfPages };
  }, [startDate, endDate, dateType, take, utcEndDate, TAKE]);

  const { data: datesWithData, isLoading: isLoadingTeeTimeDate } =
    api.searchRouter.checkTeeTimesAvailabilityForDateRange.useQuery(
      {
        dates: [],
        courseId: course?.id ?? "",
        startTime: startTime[0],
        endTime: startTime[1],
        minDate: startDate,
        maxDate: endDate,
        holes: holes === "Any" || holes === "18" ? 18 : 9,
        golfers: golfers === "Any" ? 1 : golfers,
        showUnlisted: showUnlisted,
        includesCart: includesCart,
        take: take,
        sortTime:
          sortValue === "Sort by time - Early to Late"
            ? "asc"
            : sortValue === "Sort by time - Late to Early"
              ? "desc"
              : "",
        sortPrice:
          sortValue === "Sort by price - Low to High"
            ? "asc"
            : sortValue === "Sort by price - High to Low"
              ? "desc"
              : "",
        timezoneCorrection: course?.timezoneCorrection,
        isHolesAny: holes === "Any",
        isGolferAny: golfers === "Any",
      },
      {
        enabled: course?.id !== undefined,
      }
    );

  useEffect(() => {
    if ((showSort || showFilters) && isMobile) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [showSort, isMobile, showFilters]);

  useEffect(() => {
    setPageNumber(1);
    setTake(TAKE);

    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [dateType, TAKE, isFirstRender]);

  useEffect(() => {
    if (!alertOffersShown && unreadOffers && Number(unreadOffers) > 0) {
      toast.info(
        <span>
          You have {unreadOffers} offers waiting in My Offers. Review your
          offers if you want to sell your time, counteroffer for a higher price,
          or decline.
        </span>,
        {
          position: "top-right",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          onClick: () => {
            router.push(`/${courseId}/my-tee-box?section=offers-received`);
          },
          theme: "light",
        }
      );
      setAlertOffersShown(true);
    }
  }, [unreadOffers, alertOffersShown, courseId, router, setAlertOffersShown]);

  const toggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);
  const toggleSort = useCallback(() => setShowSort((prev) => !prev), []);
  const toggleDates = useCallback(() => setShowDates((prev) => !prev), []);

  const handleLoading = useCallback((val: boolean) => setIsLoading(val), []);

  useEffect(() => {
    setPageNumber(1);
  }, [priceRange]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("googlestate");
      localStorage.removeItem("credentials");
      localStorage.removeItem("linkedinstate");
      localStorage.removeItem("facebookstate");
      localStorage.removeItem("applestate");
      microsoftClarityEvent({
        action: ``,
        category: "",
        label: "",
        value: "",
        additionalContent: {
          courseName: course?.name,
          websiteURL: course?.websiteURL,
        },
      });
    }
  }, [course?.name, course?.websiteURL]);

  const getWeekends = useCallback((dates: string[]): string[] => {
    return Array.isArray(dates)
      ? dates.filter((dateStr) => {
        return (
          dateStr.includes("Fri") ||
          dateStr.includes("Sat") ||
          dateStr.includes("Sun")
        );
      })
      : [];
  }, []);

  const datesArr: string[] = useMemo(() => {
    const sortDirection = dateType === "Furthest Day Out To Book" ? "desc" : "asc";
    let baseDates = daysData.arrayOfDates ?? [];
    if (dateType === "This Weekend") {
      baseDates = getWeekends(baseDates);
    }
    if (isLoadingTeeTimeDate) {
      return [];
    }
    if (!datesWithData || datesWithData.length === 0) {
      return [];
    }
    const datesFromApi =
      dateType === "This Weekend"
        ? getWeekends(datesWithData)
        : [...datesWithData];
    datesFromApi.sort((a: string, b: string) => {
      const timeA = new Date(a).getTime();
      const timeB = new Date(b).getTime();
      return sortDirection === "desc" ? timeB - timeA : timeA - timeB;
    });
    return datesFromApi;
  }, [
    datesWithData,
    daysData.arrayOfDates,
    dateType,
    isLoadingTeeTimeDate,
    getWeekends,
  ]);

  const amountOfPage = useMemo(() => {
    return Math.ceil(
      (datesArr.length === 0 ? 1 : datesArr.length) / TAKE
    );
  }, [datesArr.length, TAKE]);

  const finalRes = useMemo(() => {
    return [...datesArr].slice((pageNumber - 1) * TAKE, pageNumber * TAKE);
  }, [datesArr, pageNumber, TAKE]);

  const [selectedDate, setSelectedDate] = useState<string | null>(startDate);

  const pageUp = useCallback(() => {
    if (isMobile) {
      if (dateType === "Furthest Day Out To Book") {
        setSelectedDate((prev) => {
          if (!prev) return null;
          const prevDate = dayjs(prev).subtract(1, "day");
          const start = dayjs(startDate);
          if (prevDate.isBefore(start, "day")) return prev;
          setPageNumber((prevPage) => prevPage - 1);
          setTake((prevTake) => prevTake - TAKE);
          scrollRef.current?.scrollIntoView({ behavior: "smooth" });
          return prevDate.toDate().toUTCString();
        });
      } else {
        setSelectedDate((prev) => {
          if (!prev) return null;
          const nextDate = dayjs(prev).add(1, "day");
          const end = dayjs(endDate);
          if (nextDate.isAfter(end, "day")) return prev;
          setPageNumber((prevPage) => prevPage + 1);
          setTake((prevTake) => prevTake + TAKE);
          scrollRef.current?.scrollIntoView({ behavior: "smooth" });
          return nextDate.toDate().toUTCString();
        });
      }
    } else {
      setPageNumber((prev) => {
        if (prev >= amountOfPage) return prev;
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
        return prev + 1;
      });
    }
  }, [isMobile, dateType, startDate, endDate, TAKE, amountOfPage]);

  const pageDown = useCallback(() => {
    if (isMobile) {
      if (dateType === "Furthest Day Out To Book") {
        setSelectedDate((prev) => {
          if (!prev) return null;
          const nextDate = dayjs(prev).add(1, "day");
          const end = dayjs(endDate);
          if (nextDate.isAfter(end, "day")) return prev;
          setPageNumber((prevPage) => prevPage + 1);
          setTake((prevTake) => prevTake + TAKE);
          scrollRef.current?.scrollIntoView({ behavior: "smooth" });
          return nextDate.toDate().toUTCString();
        });
      } else {
        setSelectedDate((prev) => {
          if (!prev) return null;
          const prevDate = dayjs(prev).subtract(1, "day");
          const start = dayjs(startDate);
          if (prevDate.isBefore(start, "day")) return prev;
          setPageNumber((prevPage) => prevPage - 1);
          setTake((prevTake) => prevTake - TAKE);
          scrollRef.current?.scrollIntoView({ behavior: "smooth" });
          return prevDate.toDate().toUTCString();
        });
      }
    } else {
      setPageNumber((prev) => {
        if (prev <= 1) return prev;
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
        return prev - 1;
      });
    }
  }, [isMobile, dateType, startDate, endDate, TAKE]);

  const [scrollY, setScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    if (typeof window === "undefined") return;
    setScrollY(window.scrollY);
  }, []);

  useEffect(() => {
    if (source) {
      setBookingSource(source.slice(0, 50));
      sessionStorage.setItem("source", source.slice(0, 50));
    }
    if (typeof window === "undefined") return;
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [source, setBookingSource, handleScroll]);

  const { data: courseException } = api.courseException.getCourseException.useQuery({
    courseId: courseId ?? "",
  });

  const getCourseException = useCallback((playDate: string): null | NotificationObject => {
    let flag = false;
    let msg: NotificationObject | null = null;
    courseException?.forEach((ce) => {
      const ceStartDate = new Date(ce.startDate);
      const ceEndDate = new Date(ce.endDate);
      const dateToCheck = new Date(playDate);
      if (dateToCheck > ceStartDate && dateToCheck < ceEndDate) {
        flag = true;
        msg = ce;
      }
    });
    return flag ? msg : null;
  }, [courseException]);

  const courseImages = useMemo(() => {
    if (!course) return [];
    return course?.images;
  }, [course]);

  const openForecastModal = useCallback(() => setIsForecastModalOpen(true), []);
  const closeForecastModal = useCallback(() => setIsForecastModalOpen(false), []);
  const divHeight =
    typeof window !== "undefined"
      ? document?.getElementById("notification-container")?.offsetHeight
      : undefined;

  const [selectedCourse, setSelectedCourse] = useState<string>("Select a course");

  const handleCourseSwitch = useCallback((courseName: string) => {
    setSelectedCourse(courseName);
    const selected =
      allSwitchCoursesData && allSwitchCoursesData.length > 0
        ? allSwitchCoursesData?.find((c) => c.name === courseName)
        : allCoursesData?.find((c) => c.name === courseName);
    if (selected) {
      allSwitchCoursesData && allSwitchCoursesData.length > 0
        ? router.push(`/${selected?.switchableCourseId}`)
        : router.push(`/${selected?.id}`);
    }
  }, [allSwitchCoursesData, allCoursesData, router]);

  useEffect(() => {
    if (!selectedDate || !startDate) return;
    if (dateType === "Furthest Day Out To Book") {
      // datesArr is [furthest, furthest-1, ...]; index = days from selectedDate to endDate
      const index = dayjs(endDate).diff(dayjs(selectedDate), "day");
      setPageNumber(Math.floor(index / TAKE) + 1);
    } else {
      const diff = dayjs(selectedDate).diff(dayjs(startDate), "day");
      setPageNumber(diff + 1);
    }
  }, [selectedDate, startDate, dateType, endDate, TAKE]);

  const dateRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedDate) {
        const ref = dateRefs.current[dayjs(selectedDate).format("YYYY-MM-DD")];
        if (ref) {
          ref.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        }
      }
    }, 100); // Waits 100ms to ensure rendering is done

    return () => clearTimeout(timeout);
  }, [selectedDate, isLoadingTeeTimeDate, isLoading, specialEventsLoading, allCoursesDataLoading]);

  useEffect(() => {
    if (dateType === "Furthest Day Out To Book") {
      // Always select the first date in the date bar (furthest out) so tee times match the visible dates
      const formattedEndDate = dayjs.utc(endDate).format("ddd, DD MMM YYYY 00:00:00 [GMT]");
      setSelectedDate(formattedEndDate);
    } else if (startDate) {
      setSelectedDate(startDate);
    }
  }, [startDate, endDate, dateType]);

  // When dates load for "Furthest Day Out To Book", sync selectedDate to the first date in the list so it matches the date bar.
  // Depend only on datesWithData — daysData.arrayOfDates is a new array reference every render, which would clobber the user's tap on the mobile date bar.
  useEffect(() => {
    if (dateType !== "Furthest Day Out To Book") return;
    if (!datesWithData?.length) return;
    const arr = [...datesWithData] as string[];
    const firstDate = arr.reverse()[0];
    if (firstDate) setSelectedDate(firstDate);
  }, [dateType, datesWithData]);

  const handleDateSelect = useCallback(
    (date: string) => {
      setSelectedDate(date);
      // Click tracking — googleAnalyticsEvent attaches course_id/course_name and
      // scopes send_to to the master + this course's property automatically.
      googleAnalyticsEvent({
        action: "btn_ttlist_date",
        params: {
          method: "tee_times_date_button",
          content_type: "tee_time_date",
          item_id: date,
        },
      });
    },
    []
  );

  return (
    <main className={`bg-secondary-white py-4 md:py-6`}>
      <LoadingContainer
        isLoading={isLoadingTeeTimeDate || isLoading || specialEventsLoading || allCoursesDataLoading}
      >
        <div></div>
      </LoadingContainer>
      {
        !isMobile &&
        <div className="flex gap-8 items-center px-4 md:px-6 mt-6">
          <div className="min-w-[19.375rem]">
            {ALLOW_COURSE_SWITCHING ? <Select
              className="w-full"
              values={
                allSwitchCoursesData && allSwitchCoursesData.length > 0
                  ? allSwitchCoursesData.map((courseItem: { name: string }) => courseItem.name)
                  : (allCoursesData ?? [])
                    .filter((courseItem: { id: string }) => courseItem.id !== course.id)
                    .map((courseItem: { name: string }) => courseItem.name)
              }
              value={selectedCourse}
              setValue={handleCourseSwitch}
            />
              : entity?.redirectToCourseFlag ? null : (
                <GoBack
                  href="/"
                  text={`Back to All Courses`}
                  gaAction="link_back_to_courses"
                />
              )}
          </div>
          <div className="flex items-center justify-between w-full">
            <div className="flex justify-between gap-4  px-4 md:px-0">
              <div className="text-secondary-black">
                {/* Showing {count?.toLocaleString() ?? "0"} tee times{" "} */}
                <span className="text-justify text-sm text-primary-gray">
                  All times shown in course time zone
                </span>
              </div>
            </div>
            {/* <Select
              value={sortValue}
              setValue={handleSetSortValue}
              values={SortOptions}
            /> */}
          </div>
        </div>
      }
      {/* <CourseTitle
        courseName={course?.name ?? ""}
        description={course?.description ?? ""}
        className="px-4 md:px-6"  
      /> */}
      {/* <CourseBanner
        className={!isMobile ? "pt-4" : ""}
      /> */}
      {/* Custom Theme Selector */}
      {themesForPicker.length > 0 && (
        <>
          {/* Mobile: floating orb theme picker */}
          <MobileThemePicker
            themes={themesForPicker}
            selectedThemeId={selectedThemeId}
            onSelectTheme={handleThemeSelect}
          />
        </>
      )}
      <section className="relative flex gap-8 pl-0 md:pl-6 md:pt-2 mx-auto w-full">
        <div
          ref={scrollRef}
          className="absolute -top-[7.5rem] md:-top-[9.2rem]"
        />
        <div className="hidden min-w-[19.375rem] flex-col md:flex mt-4">
          <div
            style={{
              top: "calc(9.5rem + 1px)",
              maxHeight: "calc(100vh - 10.5rem)",
            }}
            className="sticky overflow-y-auto overflow-x-hidden flex flex-col gap-4"
          >
            {/* Desktop Theme Selector — above filters */}
            {themesForPicker.length > 0 && (
              <DesktopThemeSelect
                className="w-full"
                themes={themesForPicker}
                selectedTheme={selectedThemeForPicker}
                onSelectTheme={handleThemeSelect}
              />
            )}
            <Filters openForecastModal={openForecastModal} />
          </div>
        </div>
        <div className={`fixed ${isNavExpanded ? "bottom-[9.6rem]" : "bottom-[4.8rem]"} left-1/2 z-10 -translate-x-1/2 md:hidden`}>
          {/* mobile  for filter/sort */}
          <FilterSort toggleFilters={toggleFilters} toggleSort={toggleSort} />
        </div>
        <div className="flex w-full flex-col gap-1 md:gap-4 overflow-x-hidden pr-0p md:pr-6">
          {/* scrollable dates  */}

          {(MOBILE_VIEW_VERSION === "v2" && isMobile) && (
            <div
              className={`w-full overflow-x-auto left-0 top-0 z-10 bg-secondary-white pt-2 pb-2 ${(courseImages?.length > 0 ? scrollY > 333 : scrollY > 45)
                ? "fixed shadow-md"
                : ""
                }`}
              style={{
                top: (courseImages?.length > 0 ? scrollY > 333 : scrollY > 45)
                  ? `${divHeight || 0}px`
                  : "auto",
              }}
            >
              <div
                className="flex gap-2 overflow-x-auto overflow-y-hidden px-2"
                style={{
                  maxWidth: "100%",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE/Edge
                }}
              >
                <div
                  className="flex gap-2 min-w-max"
                  style={{
                    display: "flex",
                  }}
                >
                  {isLoadingTeeTimeDate || isLoading || specialEventsLoading || allCoursesDataLoading
                    ? Array.from({ length: 7 }).map((_, idx) => (
                      <div
                        key={`skeleton-${idx}`}
                        className="w-12 h-20 bg-gray-200 rounded-lg animate-pulse"
                      />
                    ))
                    : datesArr.map((dateStr: string, idx: number) => {
                      const dateObj = dayjs.utc(dateStr);
                      const isSelected =
                        selectedDate &&
                        dayjs(selectedDate).utc().format("YYYY-MM-DD") ===
                        dateObj.format("YYYY-MM-DD");
                      return (
                        <button
                          ref={(el) => {
                            if (el) dateRefs.current[dateObj.format("YYYY-MM-DD")] = el;
                          }}
                          key={`${dateObj.format("YYYY-MM-DD")}-${idx}`}
                          onClick={() => handleDateSelect(dateStr)}
                          className={`flex flex-col items-center justify-center rounded-lg px-2 text-sm border transition-all shadow-sm
              ${isSelected
                              ? "text-white font-semibold"
                              : "bg-white text-primary-black border-gray-300 hover:bg-gray-100"
                            }`}
                          style={{
                            borderColor: isSelected ? "var(--primary-color)" : "rgb(255 255 255)",
                            backgroundColor: isSelected ? "var(--primary-color)" : "rgb(255 255 255)",
                          }}
                        >
                          <span className="text-md font-med tracking-wide">
                            {dateObj.format("MMM")}
                          </span>
                          <span className="text-xl font-bold leading-tight">
                            {dateObj.format("D")}
                          </span>
                          <span className="text-[0.8125rem] font-medium">
                            {dateObj.format("ddd")}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {error ? (
            <div className="flex justify-center items-center h-[12.5rem]">
              <div className="text-center">Error: {error}</div>
            </div>
          ) : datesArr?.length === 0 ? (
            <div className="flex justify-center items-center h-[12.5rem]">
              <div className="text-center">
                {isLoadingTeeTimeDate
                  ? "Loading..."
                  : "No Tee Times Available."}
              </div>
            </div>
          ) : (
            MOBILE_VIEW_VERSION === "v2" && isMobile ?
              <>
                <div className="flex w-full flex-col gap-1 md:gap-4" ref={ref}>
                  <ViewportList viewportRef={ref} items={finalRes}>
                    {(date, idx) => (
                      <DailyTeeTimesMobileV2
                        setError={(e: string | null) => {
                          setError(e);
                        }}
                        courseException={getCourseException(date)}
                        key={idx}
                        date={selectedDate || date}
                        minDate={startDate.toString()}
                        maxDate={endDate.toString()}
                        handleLoading={handleLoading}
                        pageUp={pageUp}
                        pageDown={pageDown}
                        scrollY={scrollY}
                        divHeight={divHeight}
                        isLoadingTeeTimeDate={isLoadingTeeTimeDate}
                        // datesWithData={datesWithData}
                        allDatesArr={datesArr}
                        toggleFilters={toggleFilters}
                      />
                    )}
                  </ViewportList>
                </div>
              </>
              :
              DESKTOP_VIEW_VERSION === "v2" ?
                <>
                  <div className="flex w-full flex-col gap-1 md:gap-4" ref={ref}>
                    <DailyTeeTimesDesktopV2
                      setError={(e: string | null) => {
                        setError(e);
                      }}
                      minDate={startDate.toString()}
                      maxDate={endDate.toString()}
                      handleLoading={handleLoading}
                      dateType={dateType}
                      dates={datesArr}
                      isLoadingTeeTimeDate={isLoadingTeeTimeDate}
                    />
                  </div>
                </>
                :
                <>
                  <div className="flex w-full flex-col gap-1 md:gap-4" ref={ref}>
                    <ViewportList viewportRef={ref} items={finalRes}>
                      {(date, idx) => (
                        <DailyTeeTimes
                          setError={(e: string | null) => {
                            setError(e);
                          }}
                          courseException={getCourseException(date)}
                          key={idx}
                          date={date}
                          minDate={startDate.toString()}
                          maxDate={endDate.toString()}
                          handleLoading={handleLoading}
                          dateType={dateType}
                        />
                      )}
                    </ViewportList>
                  </div>
                  {amountOfPage > 1 ? (
                    <div className="flex items-center justify-center gap-2 pt-1 md:pt-0 md:pb-4">
                      <FilledButton
                        className={`!px-3 !py-2 !min-w-fit !rounded-md ${pageNumber === 1 ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        onClick={pageDown}
                        data-testid="chevron-down-id"
                      >
                        <ChevronUp fill="#fff" className="-rotate-90" />
                      </FilledButton>
                      <div className="text-primary-gray px-3 py-2 bg-[#ffffff] rounded-md unmask-pagination">
                        {pageNumber} / {amountOfPage}
                      </div>
                      <FilledButton
                        className={`!px-3 !py-2 !min-w-fit !rounded-md ${pageNumber === amountOfPage
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                          }`}
                        onClick={pageUp}
                        data-testid="chevron-up-id"
                      >
                        <ChevronUp fill="#fff" className="rotate-90" />
                      </FilledButton>
                    </div>
                  ) : null}
                </>
          )}
        </div>
      </section>

      {/* {showSort && (
        <MobileSort
          setShowSort={setShowSort}
          toggleSort={toggleSort}
          setSortValue={handleSetSortValue}
          sortValue={sortValue}
        />
      )} */}
      {
        showFilters && (
          <MobileFilters
            setShowFilters={setShowFilters}
            toggleFilters={toggleFilters}
            openForecastModal={openForecastModal}
          />
        )
      }
      {
        showDates && (
          <MobileDates
            setShowFilters={setShowDates}
            toggleFilters={toggleDates}
          />
        )
      }
      {
        isForecastModalOpen && (
          <ForecastModal
            closeForecastModal={closeForecastModal}
            startDate={startDate}
            endDate={endDate}
          />
        )
      }
    </main >
  );
}
